import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import Input from '../../components/Input';
import { useForgotPasswordMutation } from '../../store/services/api';
import { toast } from 'react-toastify';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const validateForm = () => {
    if (!email) {
      setError('Email is required');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await forgotPassword({ email }).unwrap();
      setIsSubmitted(true);
      toast.success('Password reset email sent successfully');
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to send password reset email';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Forgot Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSubmitted
              ? "We've sent a password reset link to your email"
              : "Enter your email and we'll send you a link to reset your password"}
          </p>
        </div>

        {!isSubmitted ? (
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="relative">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                error={error}
                className="pl-10"
                disabled={isLoading}
              />
              <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
            </div>

            <button
              type="submit"
              className="w-full py-2.5 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-[#FF4444] hover:bg-[#FF3333] transition-colors disabled:bg-opacity-70 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Sending email...' : 'Send reset link'}
            </button>
          </form>
        ) : (
          <div className="text-center">
            <p className="text-green-600 mb-5">
              We've sent instructions to reset your password to <strong>{email}</strong>. 
              Please check your inbox and follow the link.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="w-full py-2.5 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-[#FF4444] hover:bg-[#FF3333] transition-colors"
            >
              Send again
            </button>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="flex items-center justify-center text-sm font-medium text-gray-600 hover:text-[#FF4444] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;